import { useState, useEffect, useMemo, useCallback } from 'react';
import { Api, Notification } from 'services';
import { TABS } from './constants';
import { mapDocumentRow, mapStudentCourseRow } from './utils';

export const useProfileTabsData = ({
    userId,
    userRowDetails,
    specificUserProfile,
    isStaffRoute,
    activeTab,
    setPageLoader,
    // Called after a course is enrolled / unassigned — refreshes the profile's
    // quickStats (Basic details "Active courses") without a page reload.
    onCoursesChanged,
    actions: {
        getStaffDocuments,
        getStudentDocuments,
        uploadStaffDocument,
        uploadStudentDocument,
        replaceStaffDocument,
        replaceStudentDocument,
        deleteStaffDocument,
        deleteStudentDocument,
        getStudentCourses,
        enrollStudentCourse,
        unassignStudentCourse,
        updateStudentBatch,
        assignStudentStandard,
        unassignStudentStandard,
        getUserActivity,
        getStaffActivityStats,
        getStudentActivityStats,
        getStudentFee,
    }
}) => {
    // Documents tab state
    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [documentModal, setDocumentModal] = useState(null);
    const [deletingDocId, setDeletingDocId] = useState(null);
    const [uploadingDocuments, setUploadingDocuments] = useState(false);

    // Courses & batches state
    const [enrolledCourses, setEnrolledCourses] = useState([]);
    const [enrolledCoursesLoading, setEnrolledCoursesLoading] = useState(false);
    // True once the courses API has answered for this user — until then the
    // tab count falls back to quickStats.activeCourses (StaffProfilePage).
    const [hasFetchedCourses, setHasFetchedCourses] = useState(false);
    const [enrollCourseModalOpen, setEnrollCourseModalOpen] = useState(false);
    const [addBatchDrawerData, setAddBatchDrawerData] = useState(null);
    const [unassignCourseModal, setUnassignCourseModal] = useState(null);
    const [unassigningCourse, setUnassigningCourse] = useState(false);
    const [changeBatchModalData, setChangeBatchModalData] = useState(null);
    const [unassignBatchModal, setUnassignBatchModal] = useState(null);
    const [unassigningBatch, setUnassigningBatch] = useState(false);

    // Activity tab state
    const [userActivities, setUserActivities] = useState([]);
    const [userActivityLoading, setUserActivityLoading] = useState(false);
    const [activityStats, setActivityStats] = useState(null);
    const [activityStatsLoading, setActivityStatsLoading] = useState(false);

    // Fee tab state
    const [studentFeeData, setStudentFeeData] = useState(null);
    const [studentFeeLoading, setStudentFeeLoading] = useState(false);
    const [selectedFeeCourseIndex, setSelectedFeeCourseIndex] = useState(0);
    const [cancelRequestModal, setCancelRequestModal] = useState(null);
    const [cancelingRequest, setCancelingRequest] = useState(false);

    // Documents
    const fetchDocuments = useCallback((onDone) => {
        if (!userId) {
            setDocuments([]);
            onDone?.();
            return;
        }
        setDocumentsLoading(true);
        setPageLoader?.(true);
        const getDocuments = isStaffRoute ? getStaffDocuments : getStudentDocuments;
        getDocuments(userId, (response, error) => {
            setDocumentsLoading(false);
            setPageLoader?.(false);
            if (response && !error) {
                const list = Array.isArray(response) ? response : (response?.data ?? []);
                setDocuments(list.map(mapDocumentRow));
            }
            onDone?.();
        });
    }, [userId, isStaffRoute, getStaffDocuments, getStudentDocuments, setPageLoader]);

    useEffect(() => {
        if (activeTab !== TABS[4].label && activeTab !== "DOCUMENTS" && activeTab !== "Documents") return;
        fetchDocuments();
    }, [activeTab, fetchDocuments]);

    // Files picked in the Add document modal (multiple selection). The API
    // takes one file per request, so files go up one after another, followed
    // by a single summary notification and one refetch. skippedCount = files
    // rejected before upload (size/type), reported in the same summary.
    const handleUploadDocuments = async (files, skippedCount = 0) => {
        if (!userId || !files?.length) return;
        const uploadDoc = isStaffRoute ? uploadStaffDocument : uploadStudentDocument;
        // uploadAndPost reports 100 twice — once from upload progress (no
        // response) and again with the server response — and can also send
        // -1 followed by 100 on a `show.type === "error"` response. Only the
        // first final (response-backed) result is handled.
        const uploadOne = (file) => new Promise((resolve) => {
            let finished = false;
            uploadDoc(userId, file, (percent, response) => {
                if (finished) return;
                if (percent === -1) {
                    finished = true;
                    resolve(false);
                } else if (percent === 100 && response) {
                    finished = true;
                    resolve(true);
                }
            });
        });
        setUploadingDocuments(true);
        setPageLoader?.(true);
        let failed = 0;
        for (const file of files) {
            if (!(await uploadOne(file))) failed += 1;
        }
        setUploadingDocuments(false);
        setPageLoader?.(false);
        const added = files.length - failed;
        if (added && (skippedCount || failed)) {
            Notification.success("Success", "Some documents were uploaded successfully, while others failed due to file size restrictions.");
        } else if (added) {
            Notification.success("Success", added > 1 ? `${added} documents added successfully` : "Document added successfully");
        } else {
            Notification.error("Error", failed > 1 ? `Failed to add ${failed} documents` : "Failed to add document");
        }
        fetchDocuments();
    };

    const handleReplaceDocument = (docId, file, callback) => {
        if (docId == null || !userId) {
            callback(false);
            return;
        }
        const replaceDoc = isStaffRoute ? replaceStaffDocument : replaceStudentDocument;
        // Same double-100 / -1-then-100 guard as handleUploadDocuments.
        let finished = false;
        replaceDoc(userId, docId, file, (percent, response) => {
            if (finished) return;
            if (percent === -1) {
                finished = true;
                Notification.error("Error", "Failed to replace document");
                callback(false);
            } else if (percent === 100 && response) {
                finished = true;
                // Close the modal first so the page loader from the refetch
                // doesn't show up behind it.
                Notification.success("Success", "Document replaced successfully");
                callback(true);
                fetchDocuments();
            }
        });
    };

    const handleDeleteDocument = (docId, onDone) => {
        if (!userId) {
            onDone?.();
            return;
        }
        setDeletingDocId(docId);
        const deleteDoc = isStaffRoute ? deleteStaffDocument : deleteStudentDocument;
        deleteDoc(userId, docId, (response, error) => {
            setDeletingDocId(null);
            if (!error) {
                // Close the confirm modal first, then refetch (page loader).
                Notification.success("Success", "Document deleted successfully");
                onDone?.();
                fetchDocuments();
            } else {
                Notification.error("Error", error?.show?.message || error?.message || "Failed to delete document");
            }
        });
    };

    // Courses & Batches
    // Depend on the id, not the profile objects — a background profile
    // refresh (after enroll / unassign) must not re-run the tab fetch.
    const coursesUserId = userId || userRowDetails?.id || userRowDetails?._id || specificUserProfile?.id;
    const fetchCourses = useCallback((onDone) => {
        const targetUserId = coursesUserId;
        if (!targetUserId || isStaffRoute) {
            setEnrolledCourses([]);
            onDone?.();
            return;
        }
        setEnrolledCoursesLoading(true);
        setPageLoader?.(true);
        getStudentCourses(targetUserId, (response, error) => {
            setEnrolledCoursesLoading(false);
            setPageLoader?.(false);
            if (response && !error) {
                const list = Array.isArray(response) ? response : (response?.data ?? []);
                setEnrolledCourses(list.map(mapStudentCourseRow));
                setHasFetchedCourses(true);
            }
            onDone?.();
        });
    }, [coursesUserId, isStaffRoute, getStudentCourses, setPageLoader]);

    useEffect(() => {
        if (activeTab !== TABS[1].label && !activeTab.startsWith("Courses & Batches")) return;
        fetchCourses();
    }, [activeTab, fetchCourses]);

    // New student opened — fall back to quickStats again until its courses load.
    useEffect(() => {
        setHasFetchedCourses(false);
        setEnrolledCourses([]);
    }, [userId]);

    const handleEnrollCourse = (payload, callback) => {
        const targetUserId = userId || userRowDetails?.id || userRowDetails?._id || specificUserProfile?.id;
        if (!targetUserId) {
            callback(false);
            return;
        }
        enrollStudentCourse(targetUserId, payload, (response, error) => {
            if (response && !error) {
                Notification.success("Success", "Student enrolled in course successfully");
                fetchCourses();
                onCoursesChanged?.();
                callback(true);
            } else {
                Notification.error("Error", error?.show?.message || error?.message || "Failed to enroll student in course");
                callback(false);
            }
        });
    };

    const handleConfirmUnassignCourse = () => {
        const targetUserId = userId || userRowDetails?.id || userRowDetails?._id || specificUserProfile?.id;
        if (!unassignCourseModal || !targetUserId) return;
        const enrollmentId = unassignCourseModal.enrollmentId || unassignCourseModal.id || unassignCourseModal._id;
        const courseType = unassignCourseModal.courseType || unassignCourseModal.type || "course";

        setUnassigningCourse(true);
        unassignStudentCourse(targetUserId, enrollmentId, courseType, (response, error) => {
            setUnassigningCourse(false);
            setUnassignCourseModal(null);
            if (response && !error) {
                fetchCourses();
                onCoursesChanged?.();
            } else if (error && !error?.response?.data?.show) {
                Notification.error("Error", error?.show?.message || error?.message || "Failed to unassign course");
            }
        });
    };

    const handleChangeStudentBatch = (payload, callback) => {
        const targetUserId = userId || userRowDetails?.id || userRowDetails?._id || specificUserProfile?.id;
        if (!changeBatchModalData || !targetUserId) {
            callback(false);
            return;
        }
        const enrollmentId = changeBatchModalData.enrollmentId || changeBatchModalData.id;
        // Assign new batch adds a standard; Change / Assign batch update the enrollment's batch.
        const isAssignNew = changeBatchModalData.mode === "assignNew";
        const request = isAssignNew
            ? (cb) => assignStudentStandard({ userId: targetUserId, standardId: payload.standardId, enrollmentId }, cb)
            : (cb) => updateStudentBatch(targetUserId, enrollmentId, {
                ...payload,
                // Batch being replaced — the batches[] row whose CHANGE was clicked.
                ...(changeBatchModalData.currentStandardId != null
                    ? { oldStandardId: changeBatchModalData.currentStandardId }
                    : {}),
            }, cb);
        request((response, error) => {
            if (response && !error) {
                // assign-standard already returns its own success snackbar from the backend.
                if (!isAssignNew) Notification.success("Success", "Batch updated successfully");
                fetchCourses();
                callback(true);
            } else {
                Notification.error("Error", error?.show?.message || error?.message || (isAssignNew ? "Failed to assign batch" : "Failed to update batch"));
                callback(false);
            }
        });
    };

    const handleConfirmUnassignBatch = () => {
        const targetUserId = userId || userRowDetails?.id || userRowDetails?._id || specificUserProfile?.id;
        if (!unassignBatchModal || !targetUserId) return;
        const { course, batch } = unassignBatchModal;
        const standardId = batch.standardId || batch.id || batch._id;
        const enrollmentId = course.enrollmentId || course.id || course._id;

        setUnassigningBatch(true);
        unassignStudentStandard({ userId: targetUserId, standardId, enrollmentId }, (response, error) => {
            setUnassigningBatch(false);
            setUnassignBatchModal(null);
            if (response && !error) {
                fetchCourses();
            } else if (error && !error?.response?.data?.show) {
                Notification.error("Error", error?.show?.message || error?.message || "Failed to unassign batch");
            }
        });
    };

    // Activity
    const fetchActivityStats = useCallback((onDone) => {
        const targetId = userId || userRowDetails?.id || specificUserProfile?.id;
        if (!targetId) {
            setActivityStats(null);
            onDone?.();
            return;
        }
        setActivityStatsLoading(true);
        const statsAction = isStaffRoute ? getStaffActivityStats : getStudentActivityStats;
        statsAction?.(targetId, (response, error) => {
            setActivityStatsLoading(false);
            if (response && !error) {
                const data = response?.data ?? response;
                setActivityStats(data);
            } else {
                setActivityStats(null);
            }
            onDone?.();
        });
    }, [userId, userRowDetails, specificUserProfile, isStaffRoute, getStaffActivityStats, getStudentActivityStats]);

    const fetchActivities = useCallback((onDone) => {
        if (!userId) {
            setUserActivities([]);
            setActivityStats(null);
            onDone?.();
            return;
        }
        setUserActivityLoading(true);
        setPageLoader?.(true);
        getUserActivity?.({ userId, disablePagination: true }, true, (response, error) => {
            setUserActivityLoading(false);
            setPageLoader?.(false);
            if (response && !error) {
                const list = Array.isArray(response)
                    ? response
                    : (response?.activity ?? response?.data?.activity ?? (Array.isArray(response?.data) ? response.data : []));
                setUserActivities(list);
            } else {
                setUserActivities([]);
            }
            onDone?.();
        });
        fetchActivityStats();
    }, [userId, getUserActivity, fetchActivityStats, setPageLoader]);

    useEffect(() => {
        if (activeTab !== TABS[5].label && activeTab !== "Activity") return;
        fetchActivities();
    }, [activeTab, fetchActivities]);

    const flatActivities = useMemo(() => {
        if (!Array.isArray(userActivities) || userActivities.length === 0) return [];
        const result = [];
        userActivities.forEach((group) => {
            if (group && Array.isArray(group.items)) {
                group.items.forEach((item) => {
                    result.push({
                        ...item,
                        monthYear: group.monthYear
                    });
                });
            } else if (group && (group.description || group.date)) {
                result.push(group);
            }
        });
        return result;
    }, [userActivities]);

    // Fees
    const fetchFee = useCallback((onDone) => {
        const targetId = userId || userRowDetails?.id || specificUserProfile?.id;
        if (!targetId || isStaffRoute) {
            setStudentFeeData(null);
            onDone?.();
            return;
        }
        setStudentFeeLoading(true);
        setPageLoader?.(true);
        getStudentFee(targetId, (response, error) => {
            setStudentFeeLoading(false);
            setPageLoader?.(false);
            if (response && !error) {
                const data = response?.data ?? response;
                setStudentFeeData(data);
            } else {
                setStudentFeeData(null);
            }
            onDone?.();
        });
    }, [userId, userRowDetails, specificUserProfile, isStaffRoute, getStudentFee, setPageLoader]);

    useEffect(() => {
        if (activeTab !== TABS[2].label && activeTab !== "Fees" && activeTab !== "FEE") return;
        fetchFee();
    }, [activeTab, fetchFee]);

    const handleConfirmCancelRequest = () => {
        if (!cancelRequestModal) return;
        const transactionId =
            cancelRequestModal?.transactionId ||
            cancelRequestModal?.transaction?.id ||
            cancelRequestModal?.id ||
            cancelRequestModal?.txnId;

        if (!transactionId) {
            Notification.error("Cancel request", "Transaction ID not found for this installment.");
            setCancelRequestModal(null);
            return;
        }

        setCancelingRequest(true);
        Api.patch(`/feev2/cancel-request/${transactionId}`).send((response, error) => {
            setCancelingRequest(false);
            setCancelRequestModal(null);
            if (!error) {
                if (!response?.show) {
                    Notification.success("Success", response?.message || "Installment cancelled successfully");
                }
                fetchFee();
            } else if (!error?.response?.data?.show) {
                Notification.error("Error", error?.response?.data?.message || error?.message || "Failed to cancel request");
            }
        });
    };

    return {
        documents,
        documentsLoading,
        documentModal,
        setDocumentModal,
        deletingDocId,
        fetchDocuments,
        handleUploadDocuments,
        uploadingDocuments,
        handleReplaceDocument,
        handleDeleteDocument,

        enrolledCourses,
        enrolledCoursesLoading,
        hasFetchedCourses,
        enrollCourseModalOpen,
        setEnrollCourseModalOpen,
        addBatchDrawerData,
        setAddBatchDrawerData,
        unassignCourseModal,
        setUnassignCourseModal,
        unassigningCourse,
        changeBatchModalData,
        setChangeBatchModalData,
        unassignBatchModal,
        setUnassignBatchModal,
        unassigningBatch,
        fetchCourses,
        handleEnrollCourse,
        handleConfirmUnassignCourse,
        handleChangeStudentBatch,
        handleConfirmUnassignBatch,

        userActivities,
        userActivityLoading,
        activityStats,
        activityStatsLoading,
        flatActivities,
        fetchActivityStats,
        fetchActivities,

        studentFeeData,
        studentFeeLoading,
        selectedFeeCourseIndex,
        setSelectedFeeCourseIndex,
        cancelRequestModal,
        setCancelRequestModal,
        cancelingRequest,
        fetchFee,
        handleConfirmCancelRequest,
    };
};
